import { addDays } from 'date-fns'

export const OSLO_TIME_ZONE = 'Europe/Oslo'

const osloDateFormatter = new Intl.DateTimeFormat('en', {
  timeZone: OSLO_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  calendar: 'gregory',
  numberingSystem: 'latn',
})

const osloTimeFormatter = new Intl.DateTimeFormat('en', {
  timeZone: OSLO_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  numberingSystem: 'latn',
})

export const toOsloDateString = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const parts = osloDateFormatter.formatToParts(d)
  const year = parts.find((p) => p.type === 'year')?.value
  const month = parts.find((p) => p.type === 'month')?.value
  const day = parts.find((p) => p.type === 'day')?.value

  if (!year || !month || !day) {
    throw new Error('Unable to format Oslo date parts')
  }

  return `${year}-${month}-${day}`
}

const parseOsloDate = (osloStr: string): Date => {
  const parts = osloStr.split('-').map(Number) as [number, number, number]
  return new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0, 0)
}

const pad = (n: number): string => String(n).padStart(2, '0')

const getEasterSunday = (year: number): { month: number; day: number } => {
  const c = Math.floor(year / 100)
  const n = year - 19 * Math.floor(year / 19)
  const k = Math.floor((c - 17) / 25)
  let i = c - Math.floor(c / 4) - Math.floor((c - k) / 3) + 19 * n + 15
  i = i - 30 * Math.floor(i / 30)
  i = i - Math.floor(i / 28) * (1 - Math.floor(i / 28) * Math.floor(29 / (i + 1)) * Math.floor((21 - n) / 11))
  let j = year + Math.floor(year / 4) + i + 2 - c + Math.floor(c / 4)
  j = j - 7 * Math.floor(j / 7)
  const l = i - j
  const m = 3 + Math.floor((l + 40) / 44)
  const d = l + 28 - 31 * Math.floor(m / 4)
  return { month: m, day: d }
}

const easterOffset = (year: number, offsetDays: number): string => {
  const easter = getEasterSunday(year)
  const d = new Date(year, easter.month - 1, easter.day + offsetDays, 12, 0, 0, 0)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const getNorwegianPublicHolidays = (year: number): Set<string> => {
  return new Set([
    `${year}-01-01`,
    easterOffset(year, -7),
    easterOffset(year, -3),
    easterOffset(year, -2),
    easterOffset(year, 0),
    easterOffset(year, 1),
    easterOffset(year, 39),
    easterOffset(year, 49),
    easterOffset(year, 50),
    `${year}-05-01`,
    `${year}-05-17`,
    `${year}-12-24`,
    `${year}-12-25`,
    `${year}-12-26`,
    `${year}-12-31`,
  ])
}

let holidayCache: { year: number; set: Set<string> } | undefined

const getHolidaySet = (year: number): Set<string> => {
  if (holidayCache?.year === year) return holidayCache.set
  const set = getNorwegianPublicHolidays(year)
  holidayCache = { year, set }
  return set
}

export const isAfterClosingTime = (date: Date): boolean => {
  const now = new Date()
  if (toOsloDateString(now) !== toOsloDateString(date)) return false
  const parts = osloTimeFormatter.formatToParts(now)
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? '0')
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? '0')
  return h * 60 + m > 15 * 60 + 30
}

export const isHoliday = (date: Date): boolean => {
  const osloDate = toOsloDateString(date)
  const year = Number(osloDate.slice(0, 4))
  return getHolidaySet(year).has(osloDate)
}

export const isPickupDateDisabled = (date: Date, minDate: Date = new Date()): boolean => {
  const dateStr = toOsloDateString(date)
  const minStr = toOsloDateString(minDate)
  if (dateStr < minStr) return true

  const day = parseOsloDate(dateStr).getDay()
  if (day === 0 || day === 6) return true

  if (isHoliday(date)) return true
  if (isAfterClosingTime(date)) return true

  return false
}

export const getNextAvailablePickupDate = (fromDate: Date, minDate: Date = new Date()): Date => {
  const minStr = toOsloDateString(minDate)
  let candidateStr = toOsloDateString(fromDate)
  if (candidateStr < minStr) candidateStr = minStr

  let candidate = parseOsloDate(candidateStr)
  while (isPickupDateDisabled(candidate, minDate)) {
    candidate = addDays(candidate, 1)
  }
  return candidate
}

export const normalizePickupDate = (date: Date, minDate: Date = new Date()): Date => {
  if (!isPickupDateDisabled(date, minDate)) return date
  return getNextAvailablePickupDate(date, minDate)
}
